import React, { useState } from 'react'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
  TablePagination,
  Typography,
  CardContent,
  Card,
  Autocomplete,
  TextField,
  Chip,
} from '@mui/material'
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon,
  InfoOutlined as InfoIcon,
  Download as DownloadIcon,
} from '@mui/icons-material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { formatToStandard, parseDate } from '../../utils/dateUtils'
import useInvoiceRecords from '../../hooks/useInvoiceRecords'
import usePermission from '../../hooks/usePermission'
import RemarkModal from './components/RemarkModal'
import InvoiceActionsModal from './components/InvoiceActionsModal'
import BulkInvoiceActionsModal from './components/BulkInvoiceActionsModal'
import './invoice-records.scss'

const InvoiceRecords = () => {
  const {
    invoices,
    loading,
    filters,
    setFilters,
    selectedInvoices,
    page,
    rowsPerPage,
    total,
    snackbar,
    plantNames,
    organizations,
    statusOptions,
    isExporting,
    handleSelectInvoice,
    handleSelectAll,
    handleUpdateInvoice,
    handleSendEmail,
    handleExportToZoho,
    handleCreateInvoice,
    handleBulkUpdate,
    handleBulkRaiseInvoices,
    handleBulkEmail,
    handleBulkZohoSync,
    closeSnackbar,
    handleChangePage,
    handleChangeRowsPerPage,
    setSelectedInvoices,
    exportToCSV,
  } = useInvoiceRecords()

  const { can } = usePermission()
  const [remarkModalOpen, setRemarkModalOpen] = useState(false)
  const [actionsModalOpen, setActionsModalOpen] = useState(false)
  const [bulkActionsModalOpen, setBulkActionsModalOpen] = useState(false)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null)
  const [screenHeight, setScreenHeight] = useState(window.innerHeight)
  const headerRef = React.useRef(null)
  const [headerHeight, setHeaderHeight] = useState(200)

  React.useEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(() => {
        setScreenHeight(window.innerHeight)
        if (headerRef.current) {
          setHeaderHeight(headerRef.current.offsetHeight)
        }
      })
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const handleViewRemark = (invoice) => {
    if (selectedInvoices.length > 1) {
      return // Disable when multiple selected
    }
    setSelectedInvoiceId(invoice._id)
    setRemarkModalOpen(true)
  }

  const handleOpenActionsModal = () => {
    if (selectedInvoices.length === 0) {
      return
    }
    setActionsModalOpen(true)
  }

  const handleCloseRemarkModal = () => {
    setRemarkModalOpen(false)
    setSelectedInvoiceId(null)
  }

  const handleCloseActionsModal = () => {
    setActionsModalOpen(false)
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0)
  }

  const isInvoiceSelected = (invoiceId) => {
    return selectedInvoices.includes(invoiceId)
  }

  const canCreate = can('invoice.create');
  const canUpdate = can('invoice.update') || can('invoice.paymentstatus.update') || can('invoice.automation.update');
  const canZohoSend = can('invoice.zoho.send');
  const hasActionAccess = canCreate || canUpdate;

  const isAllSelected = () => {
    const currentPageInvoices = invoices.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    )
    return (
      currentPageInvoices.length > 0 &&
      currentPageInvoices.every((inv) => selectedInvoices.includes(inv._id))
    )
  }

  // Get selected invoice objects
  const getSelectedInvoiceObjects = () => {
    return invoices.filter((inv) => selectedInvoices.includes(inv._id))
  }

  const handleRowCheckboxChange = (invoiceId) => {
    setSelectedInvoices((prev) => {
      if (prev.includes(invoiceId)) {
        return prev.filter((id) => id !== invoiceId)
      } else {
        return [...prev, invoiceId]
      }
    })
  }

  const handleHeaderCheckboxChange = (event) => {
    if (event.target.checked) {
      const currentPageInvoices = invoices
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((inv) => inv._id)
      setSelectedInvoices(currentPageInvoices)
    } else {
      setSelectedInvoices([])
    }
  }

  const tableHeight = screenHeight - headerHeight - 150

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="invoice-records-container">
        {/* Header */}
        <Box ref={headerRef} className="invoice-records-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h4" component="h1" className="page-title">
            Invoice Records
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={isExporting ? <CircularProgress size={16} /> : <DownloadIcon />}
              onClick={exportToCSV}
              disabled={invoices.length === 0 || isExporting}
            >
              Download ({invoices.length})
            </Button>
            {hasActionAccess && selectedInvoices.length > 1 && (
              <Button
                variant="contained"
                color="secondary"
                disabled={!canCreate && getSelectedInvoiceObjects().some(inv => inv.recordType === 'BILLING')}
                startIcon={<EditIcon />}
                onClick={() => setBulkActionsModalOpen(true)}
              >
                Bulk Update ({selectedInvoices.length})
              </Button>
            )}
          </Box>
        </Box>

        {/* Filters Card */}
        <Card className="filters-card">
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {/* Plant Name Filter */}
              <Autocomplete
                size="small"
                sx={{ minWidth: 200 }}
                options={plantNames}
                value={filters.plantName || null}
                onChange={(e, newValue) =>
                  setFilters({ ...filters, plantName: newValue || '' })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Plant Name" />
                )}
              />

              {/* Organization Filter */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={filters.organization}
                  label="Organization"
                  onChange={(e) =>
                    setFilters({ ...filters, organization: e.target.value })
                  }
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {organizations.map((org) => (
                    <MenuItem key={org} value={org}>
                      {org}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Status Filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) =>
                    setFilters({ ...filters, status: e.target.value })
                  }
                >
                  <MenuItem value="">
                    <em>ALL</em>
                  </MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Period Filter (MM-YYYY) */}
              <DatePicker
                label="Month"
                views={['year', 'month']}
                value={filters.period}
                onYearChange={() => { /* Prevent automatic update */ }}
                onMonthChange={(newDate) => {
                  if (newDate && newDate.isValid()) {
                    setFilters(prev => ({ ...prev, period: newDate }))
                  }
                }}
                onChange={(newValue) => {
                  if (newValue === null) {
                    setFilters(prev => ({ ...prev, period: null }))
                  }
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { minWidth: 200 },
                  },
                  field: {
                    clearable: true,
                  },
                }}
              />

              {/* Auto Reminder Filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Auto Reminder</InputLabel>
                <Select
                  value={filters.autoReminder || ''}
                  label="Auto Reminder"
                  onChange={(e) =>
                    setFilters({ ...filters, autoReminder: e.target.value })
                  }
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>

              {/* Email Sent Filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Email Sent</InputLabel>
                <Select
                  value={filters.emailSent || ''}
                  label="Email Sent"
                  onChange={(e) =>
                    setFilters({ ...filters, emailSent: e.target.value })
                  }
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>

              {/* Zoho Export Filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Zoho Export</InputLabel>
                <Select
                  value={filters.zohoExported || ''}
                  label="Zoho Export"
                  onChange={(e) =>
                    setFilters({ ...filters, zohoExported: e.target.value })
                  }
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="Yes">Yes</MenuItem>
                  <MenuItem value="No">No</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ flexGrow: 1 }} />




            </Box>
          </CardContent>
        </Card>

        {/* Table */}
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Card className="invoice-table-card">
            <CardContent>
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <TableContainer component={Paper} sx={{ maxHeight: tableHeight }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isAllSelected()}
                            indeterminate={
                              selectedInvoices.length > 0 && !isAllSelected()
                            }
                            onChange={handleHeaderCheckboxChange}
                          />
                        </TableCell>
                        <TableCell>
                          <strong>Plant Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Organization</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Invoice Number</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Invoice Date</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Due Date</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Amount</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Remark</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Status</strong>
                        </TableCell>
                        {hasActionAccess && (
                          <TableCell align="center">
                            <strong>Action</strong>
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((invoice) => {
                          const isUnpaid =
                            (invoice.status?.toUpperCase() === 'UNPAID') ||
                            (invoice.paymentStatus?.toUpperCase() === 'UNPAID')
                          const isPaid =
                            (invoice.status?.toUpperCase() === 'PAID') ||
                            (invoice.paymentStatus?.toUpperCase() === 'PAID')
                          return (
                            <TableRow
                              key={invoice._id}
                              sx={{
                                backgroundColor: isPaid
                                  ? 'rgba(0, 200, 0, 0.12)'
                                  : isUnpaid
                                    ? 'rgba(255, 0, 0, 0.1)'
                                    : invoice.recordType === 'BILLING'
                                      ? 'rgba(255, 235, 59, 0.15)' // Yellow for Billing Records
                                      : 'inherit',
                                '&:hover': {
                                  backgroundColor: isPaid
                                    ? 'rgba(0, 200, 0, 0.18)'
                                    : isUnpaid
                                      ? 'rgba(255, 0, 0, 0.15)'
                                      : invoice.recordType === 'BILLING'
                                        ? 'rgba(255, 235, 59, 0.25)'
                                        : 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={isInvoiceSelected(invoice._id)}
                                  onChange={() =>
                                    handleRowCheckboxChange(invoice._id)
                                  }
                                />
                              </TableCell>
                              <TableCell>{invoice.plantName || 'N/A'}</TableCell>
                              <TableCell>
                                {invoice.organizationName || invoice.organizationId || 'N/A'}
                              </TableCell>
                              <TableCell>
                                {invoice.invoiceNumber || 'N/A'}
                              </TableCell>
                              <TableCell>
                                {invoice.invoiceDate
                                  ? formatToStandard(invoice.invoiceDate)
                                  : 'N/A'}
                              </TableCell>
                              <TableCell>
                                {invoice.dueDate
                                  ? formatToStandard(invoice.dueDate)
                                  : 'N/A'}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <span>{formatCurrency(invoice.invoiceValue)}</span>
                                  <Tooltip title="Currency: INR" arrow placement="top">
                                    <InfoIcon sx={{ fontSize: 16, color: '#ff9800', cursor: 'pointer' }} />
                                  </Tooltip>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                {invoice.recordType !== 'BILLING' ? (
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleViewRemark(invoice)}
                                    disabled={selectedInvoices.length > 1}
                                  >
                                    <ViewIcon />
                                  </IconButton>
                                ) : (
                                  <Box sx={{ width: 34, height: 34 }} />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={
                                    (invoice.paymentStatus || invoice.status)?.toUpperCase() === 'TO BE RAISED' &&
                                      invoice.invoiceDate &&
                                      (parseDate(invoice.invoiceDate).isBefore(parseDate(), 'day') || parseDate(invoice.invoiceDate).isSame(parseDate(), 'day'))
                                      ? 'To Be Raised'
                                      : (invoice.paymentStatus || invoice.paymentStatus || 'N/A')
                                  }
                                  color={
                                    (invoice.paymentStatus || invoice.paymentStatus)?.toUpperCase() === 'PAID'
                                      ? 'success'
                                      : (invoice.paymentStatus || invoice.paymentStatus)?.toUpperCase() === 'UNPAID'
                                        ? 'error'
                                        : (invoice.paymentStatus || invoice.paymentStatus)?.toUpperCase() === 'TO BE RAISED'
                                          ? (parseDate(invoice.invoiceDate).isBefore(parseDate(), 'day') || parseDate(invoice.invoiceDate).isSame(parseDate(), 'day') ? 'warning' : 'default')
                                          : 'warning'
                                  }
                                  size="small"
                                />
                              </TableCell>
                              {
                                hasActionAccess && (
                                  <TableCell align="center">
                                    <IconButton
                                      size="small"
                                      color={(!canCreate && invoice.recordType === 'BILLING') ? 'default' : 'primary'}
                                      disabled={!canCreate && invoice.recordType === 'BILLING'}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        if (!canCreate && invoice.recordType === 'BILLING') {
                                          // Logic handled by disabled attribute, or we can show snackbar here if not disabled
                                          return;
                                        }
                                        // Clear all selections and select only this invoice
                                        setSelectedInvoices([invoice._id])
                                        setTimeout(() => {
                                          setActionsModalOpen(true)
                                        }, 50)
                                      }}
                                    >
                                      <EditIcon />
                                    </IconButton>
                                  </TableCell>
                                )
                              }
                            </TableRow>
                          )
                        })}

                      {invoices.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Typography variant="body1" color="text.secondary">
                              No invoices found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>
        )}

        {/* Modals */}
        <RemarkModal
          open={remarkModalOpen}
          onClose={handleCloseRemarkModal}
          invoiceId={selectedInvoiceId}
        />

        <InvoiceActionsModal
          open={actionsModalOpen}
          onClose={handleCloseActionsModal}
          selectedInvoices={getSelectedInvoiceObjects()}
          period={filters.period}
          onUpdate={handleUpdateInvoice}
          onCreate={handleCreateInvoice}
          onSendEmail={handleSendEmail}
          onExportToZoho={handleExportToZoho}
          canCreate={canCreate}
          canUpdate={canUpdate}
          canZohoSend={canZohoSend}
        />

        <BulkInvoiceActionsModal
          open={bulkActionsModalOpen}
          onClose={() => setBulkActionsModalOpen(false)}
          selectedInvoices={getSelectedInvoiceObjects()}
          onBulkUpdate={handleBulkUpdate}
          onBulkRaise={handleBulkRaiseInvoices}
          onBulkEmail={handleBulkEmail}
          onBulkZohoSync={handleBulkZohoSync}
          canCreate={canCreate}
          canUpdate={canUpdate}
          canZohoSend={canZohoSend}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%', whiteSpace: 'pre-line' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider >
  )
}

export default InvoiceRecords
